 import { lazy, Suspense } from "react";
 const CuradoriaPage = lazy(() => import("./pages/CuradoriaPage.tsx"));
 const UserArticlesPage = lazy(() => import("./pages/UserArticlesPage.tsx"));
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider } from "@/contexts/AppContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import GenerationBanner from "./components/member/GenerationBanner.tsx";
import LegalAcceptanceGate from "./components/member/LegalAcceptanceGate";
import PushOnboardingBanner from "./components/member/PushOnboardingBanner";
import GlobalBackButton from "./components/GlobalBackButton";
import AppInstallBanner from "./components/AppInstallBanner";
import GlobalHeader from "./components/GlobalHeader";

// Eager-load only LoginPage (high-traffic auth entry).
// Index is lazy because the root route is LandingPage — eager-loading
// Index pulls MemberHome + deps into the initial bundle for visitors
// who never reach /app, wasting ~30KB on the landing.
import LoginPage from "./pages/LoginPage.tsx";
import RootRedirect from "./pages/RootRedirect.tsx";
const Index = lazy(() => import("./pages/Index.tsx"));

// Code-split secondary routes — keeps initial bundle minimal
const Admin = lazy(() => import("./pages/Admin.tsx"));
const NotePage = lazy(() => import("./pages/NotePage.tsx"));
const DevotionalPage = lazy(() => import("./pages/DevotionalPage.tsx"));
const AnnotationsRoute = lazy(() => import("./pages/AnnotationsRoute.tsx"));
const SignupPage = lazy(() => import("./pages/SignupPage.tsx"));
const CompleteProfilePage = lazy(() => import("./pages/CompleteProfilePage.tsx"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage.tsx"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage.tsx"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage.tsx"));
const ProfileRoute = lazy(() => import("./pages/ProfileRoute.tsx"));
const GeneratePage = lazy(() => import("./pages/GeneratePage.tsx"));
const PublicNotePage = lazy(() => import("./pages/PublicNotePage.tsx"));
const JornadaPage = lazy(() => import("./pages/JornadaPage.tsx"));
const AssinaturaPage = lazy(() => import("./pages/AssinaturaPage.tsx"));
const AssinaturaSucessoPage = lazy(() => import("./pages/AssinaturaSucessoPage.tsx"));
const NotificacoesPage = lazy(() => import("./pages/NotificacoesPage.tsx"));
const TermosPage = lazy(() => import("./pages/TermosPage.tsx"));
const PrivacidadePage = lazy(() => import("./pages/PrivacidadePage.tsx"));
const ColecoesPage = lazy(() => import("./pages/ColecoesPage.tsx"));
const LivrosPage = lazy(() => import("./pages/LivrosPage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const InstallPage = lazy(() => import("./pages/InstallPage.tsx"));
const PresentePage = lazy(() => import("./pages/PresentePage.tsx"));
const PresenteSucessoPage = lazy(() => import("./pages/PresenteSucessoPage.tsx"));
const BuscaPage = lazy(() => import("./pages/BuscaPage.tsx"));
const TrendingPage = lazy(() => import("./pages/TrendingPage.tsx"));
const SeriesIndexPage = lazy(() => import("./pages/SeriesIndexPage.tsx"));
const SeriesDetailPage = lazy(() => import("./pages/SeriesDetailPage.tsx"));
const BibliotecaPage = lazy(() => import("./pages/BibliotecaPage.tsx"));
const IgrejaPage = lazy(() => import("./pages/IgrejaPage.tsx"));
const SobrePage = lazy(() => import("./pages/SobrePage.tsx"));
const ComoFuncionaPage = lazy(() => import("./pages/ComoFuncionaPage.tsx"));
const ArticlesPage = lazy(() => import("./pages/ArticlesPage.tsx"));
const ArticleDetailPage = lazy(() => import("./pages/ArticleDetailPage.tsx"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AppProvider>
        <LanguageProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <LegalAcceptanceGate>
              <div className="flex flex-col min-h-screen">
                <GlobalHeader />
                <div className="flex-1">
                <Suspense fallback={null}>
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/home" element={<LandingPage />} />
                    <Route path="/app" element={<Index />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/complete-profile" element={<CompleteProfilePage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/note/:id" element={<NotePage />} />
                    <Route path="/note/:id/devotional" element={<DevotionalPage />} />
                    <Route path="/nota/:id" element={<PublicNotePage />} />
                    <Route path="/annotations" element={<AnnotationsRoute />} />
                    <Route path="/notificacoes" element={<NotificacoesPage />} />
                    <Route path="/termos" element={<TermosPage />} />
                    <Route path="/privacidade" element={<PrivacidadePage />} />
                    <Route path="/colecoes" element={<ColecoesPage />} />
                    <Route path="/colecoes/:id" element={<ColecoesPage />} />
                    <Route path="/livros" element={<LivrosPage />} />
                    <Route path="/generate" element={<GeneratePage />} />
                    <Route path="/profile" element={<ProfileRoute />} />
                    <Route path="/assinatura" element={<AssinaturaPage />} />
                    <Route path="/curadoria" element={<CuradoriaPage />} />
                    <Route path="/assinatura/sucesso" element={<AssinaturaSucessoPage />} />
                    <Route path="/instalar" element={<InstallPage />} />
                    <Route path="/jornada" element={<JornadaPage />} />
                    <Route path="/presente" element={<PresentePage />} />
                    <Route path="/presente/sucesso" element={<PresenteSucessoPage />} />
                    <Route path="/busca" element={<BuscaPage />} />
                    <Route path="/em-alta" element={<TrendingPage />} />
                    <Route path="/series" element={<SeriesIndexPage />} />
                    <Route path="/series/:slug" element={<SeriesDetailPage />} />
                    <Route path="/biblioteca" element={<BibliotecaPage />} />
                    <Route path="/igrejas/:slug" element={<IgrejaPage />} />
                    <Route path="/sobre" element={<SobrePage />} />
                    <Route path="/como-funciona" element={<ComoFuncionaPage />} />
                     <Route path="/artigos" element={<ArticlesPage />} />
                    <Route path="/artigo/:slug" element={<ArticleDetailPage />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                </div>
              </div>
              <GlobalBackButton />
              <GenerationBanner />
              <PushOnboardingBanner />
              <AppInstallBanner />
            </LegalAcceptanceGate>
          </BrowserRouter>
        </LanguageProvider>
      </AppProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
